import React, { useState, useEffect } from 'react';
import proptypes from 'prop-types';
const { oneOfType, object, array, func } = proptypes;
import { useConfigService } from '@thd-nucleus/experience-context';
import { useDesignStore } from '../../store/design/context';
import { createDesign, saveDesign, tryGetDesign } from '../../api/design';
import {
  getUrlAddress,
  tryGetQueryParam,
  setQueryParam,
  deleteQueryParam
} from '../../utils/queryParams';
import { saveDTO, getDTO } from '../../utils/jsonHelpers';
import { validateUUID } from '../../utils/validateUUID';
import logNewRelicCustom from '../../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../../constants';

const Orchestrator = ({ metadata, setHydrated }) => {
  const designIdFromParams = tryGetQueryParam('designId');
  const host = useConfigService('global-custom-commerce:host');
  const design = useDesignStore((state) => state.design);
  const updateDesign = useDesignStore((state) => state.update);
  const loadDesignFromDocument = useDesignStore((state) => state.loadDesignFromDocument);
  const { designId, products, cart } = design;
  const [existingDesign, setExistingDesign] = useState(Boolean(designId));
  const clearStorage = useDesignStore((state) => state.clearStorage);
  useEffect(() => {
    const getDesignAsync = async () => {
      const newDesignId = await createDesign(host, 15, 'Packout');
      logNewRelicCustom(NEW_RELIC_EVENTS.CREATED_DESIGN, { createdDesignId: newDesignId });
      return newDesignId;
    };

    const saveDesignAsync = async () => {
      const dto = await saveDTO(design);
      const response = await saveDesign(host, 15, 'Packout', { ...dto });
      logNewRelicCustom(NEW_RELIC_EVENTS.SAVED_DESIGN, design);
      return response;
    };

    const setDesignIdAndUpdateAsync = async () => {
      try {
        const newDesignId = await getDesignAsync();
        if (!newDesignId) {
          setExistingDesign(true);
          return;
        }
        setQueryParam('designId', newDesignId);
        updateDesign({ designId: newDesignId });
        setExistingDesign(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(`Create Design Error: ${err}`);
      }
    };

    if (
      !designId
      && (products.length > 0 || cart.length > 0)
      && !designIdFromParams
      && !existingDesign
    ) {
      setDesignIdAndUpdateAsync();
      return;
    }

    if (
      designId
      && validateUUID(designId)
      && designIdFromParams
      && validateUUID(designIdFromParams)
    ) {
      saveDesignAsync();
    }

  }, [designId, products.length, cart]);

  useEffect(() => {
    const getDesignDocument = async () => {
      const designDocument = await tryGetDesign(host, designIdFromParams);
      if (!designDocument) {
        deleteQueryParam('designId');
        updateDesign({ designId: null });
        return setHydrated({
          design: true,
          graph: false
        });
      }

      if (designIdFromParams && designIdFromParams !== designId) {
        logNewRelicCustom(NEW_RELIC_EVENTS.LOAD_SHARED_DESIGN, {
          designIdLoaded: designIdFromParams,
          url: getUrlAddress()
        });
        // this is a shared design clone it
        const newDesignId = await createDesign(host, 15, 'Packout');
        designDocument.id = newDesignId;
        setQueryParam('designId', newDesignId);
      }

      const dto = getDTO(metadata, designDocument);
      if (dto) {
        loadDesignFromDocument(dto);
        logNewRelicCustom(
          NEW_RELIC_EVENTS.DESIGN_LOADED,
          { source: 'Design API using DesignId from Params', designId: designIdFromParams, host }
        );
      }
      return setHydrated({
        design: true,
        graph: false
      });
    };

    const getDesignDocumentWithoutParams = async () => {
      const designDocument = await tryGetDesign(host, designId);
      if (designDocument) {
        const dto = getDTO(metadata, designDocument);
        if (dto?.design) {
          loadDesignFromDocument(dto);
          setQueryParam('designId', designId);
          logNewRelicCustom(
            NEW_RELIC_EVENTS.DESIGN_LOADED,
            { source: 'Design API using DesignId from State', designId, host }
          );
        }
      }
    };

    const goodDesignId = !!designId && validateUUID(designId);
    const goodParamDesignId = !!designIdFromParams && validateUUID(designIdFromParams);

    if (!goodDesignId && !goodParamDesignId) {

      if (clearStorage) clearStorage();
      deleteQueryParam('designId');
      updateDesign({ designId: null });
      return setHydrated({
        design: true,
        graph: false
      });
    }

    if (goodDesignId && !goodParamDesignId) {
      deleteQueryParam('designId');
      getDesignDocumentWithoutParams();
      updateDesign({ designId });
      return setHydrated({
        design: true,
        graph: false
      });
    }

    if (!goodDesignId && goodParamDesignId) {
      getDesignDocument();
      updateDesign({ designId: designIdFromParams });
      return setHydrated({
        design: true,
        graph: false
      });
    }

    getDesignDocument();

    return setHydrated({
      design: true,
      graph: false
    });
  }, []);

  return (
    <div id="orchestrator" />
  );
};

Orchestrator.propTypes = {
  metadata: oneOfType([object, array]).isRequired,
  setHydrated: func.isRequired
};

export default Orchestrator;
